// Periodos.tsx
//
// Aba "Períodos" de Lojas. Período é o turno de caixa da loja — "Almoço",
// "Jantar" —, a janela dentro da qual os pedidos são lançados e o caixa é
// fechado. É o mesmo "próximo período" que os Meios de Pagamento usam para
// decidir quando a configuração desce para o PDV.
//
// Esta tela existe por um motivo simples: o PeriodTypeModal (o modal mais
// completo do repo, 7 seções) estava no código e NENHUMA entrada do sistema o
// abria. Aqui está a entrada.
//
// De onde os períodos vêm: do `decryptedInfoToken`, não de HTTP — travessia
// organization -> companies -> subsidiaries -> periodTypes, o mesmo caminho de
// Ambients.tsx e MeiosPagamento.tsx. Não há rota de período no backend para
// buscar (nem para gravar — ver o aviso no rodapé).
import React, { useMemo } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Periodos";

interface PeriodosProps {
  decryptedInfoToken: any;
  handleOpenModal: (type: string, object?: any) => void;
}

/**
 * Data vinda do token é string ISO — mas pode ser nula, e já veio quebrada.
 * `new Date("qualquer coisa").toLocaleString()` devolve "Invalid Date" na cara
 * do dono; aqui vira um traço.
 */
function formatarDataHora(valor?: string | null): string {
  if (!valor) return "—";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "—";
  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * `periodTypeInfo` é um array no token, mas pode vir ausente em período recém
 * criado — daí o `?? []` antes de contar.
 */
function contarConfiguracoes(periodo: any): string {
  const quantas = (periodo?.periodTypeInfo ?? []).length;
  return quantas === 1
    ? "1 configuração gravada"
    : `${quantas} configurações gravadas`;
}

const Periodos: React.FC<PeriodosProps> = ({
  decryptedInfoToken,
  handleOpenModal,
}) => {
  // Travessia organization -> companies -> subsidiaries -> periodTypes.
  // O hook do token devolve null no primeiro render, então tudo com `?.`.
  //
  // O agrupamento é POR LOJA (e não uma lista chapada de períodos) porque
  // "Almoço" da loja A e "Almoço" da loja B são linhas diferentes de
  // `acd."ActPeriodType"`: sem a loja em volta, dois cartões com o mesmo nome
  // pareceriam duplicata.
  //
  // O `.filter` por `company` não é zelo: o backend monta o token com
  // `companies.map(... subsidiaries.map(...))` SEM filtrar a filial pela
  // empresa (organization.controller.ts), então cada empresa recebe TODAS as
  // filiais da organização. Sem o filtro, um dono com duas empresas veria a
  // mesma loja duas vezes, a segunda com o nome da empresa errada em cima — e
  // com `key` repetida. O filtro só age quando o `companyUuid` existe, para
  // não esvaziar a lista se o formato mudar.
  const lojas: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? [])
        .filter((filial: any) =>
          empresa?.companyUuid ? filial?.company === empresa.companyUuid : true
        )
        .map((filial: any) => ({
          subsidiaryUuid: filial?.subsidiaryUuid,
          subsidiaryName: filial?.subsidiaryName,
          companyName: empresa?.companyName,
          periodTypes: (filial?.periodTypes ?? []).map((periodo: any) => ({
            ...periodo,
            // Carregados junto porque o modal e os cartões precisam saber a que
            // loja o período pertence — o objeto do período não guarda o nome.
            storeName: filial?.subsidiaryName,
            companyName: empresa?.companyName,
          })),
        }))
    );
  }, [decryptedInfoToken]);

  const totalPeriodos = useMemo(
    () => lojas.reduce((soma, loja) => soma + loja.periodTypes.length, 0),
    [lojas]
  );

  // Só as lojas que TÊM turno. O rodapé dizia "N períodos em M lojas" com M =
  // todas as lojas cadastradas: com 3 lojas e 1 delas configurada, saía
  // "2 períodos em 3 lojas", como se os turnos estivessem espalhados pelas
  // três. A conta certa é onde os períodos de fato estão.
  const lojasComPeriodo = useMemo(
    () => lojas.filter((loja) => loja.periodTypes.length > 0).length,
    [lojas]
  );

  if (!decryptedInfoToken) {
    return (
      <AlfredoStyles.FieldsContainer>
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando períodos…</div>
        </AlfredoStyles.LoadingContainer>
      </AlfredoStyles.FieldsContainer>
    );
  }

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              schedule
            </AlfredoStyles.MaterialIcons>
            Períodos
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            Um período é o turno de caixa da loja — "Almoço", "Jantar" —, a
            janela em que os pedidos são lançados até o caixa daquele turno
            fechar.
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>

        <AlfredoStyles.HeaderActions>
          <AlfredoStyles.Button onClick={() => handleOpenModal("periodType")}>
            <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
              add
            </AlfredoStyles.MaterialIcons>
            Novo Período
          </AlfredoStyles.Button>
        </AlfredoStyles.HeaderActions>
      </AlfredoStyles.Header>

      {lojas.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🏪</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhuma loja cadastrada
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            Período pertence a uma loja. Cadastre uma loja em "Lojas" e ela
            aparece aqui.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : totalPeriodos === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🕒</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhum período cadastrado
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            As lojas existem, mas nenhuma tem turno de caixa configurado.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        lojas.map((loja) => (
          <AlfredoStyles.LojaCard key={loja.subsidiaryUuid}>
            <AlfredoStyles.LojaTitulo>
              <AlfredoStyles.MaterialIcons>store</AlfredoStyles.MaterialIcons>
              {loja.subsidiaryName}
            </AlfredoStyles.LojaTitulo>
            <AlfredoStyles.LojaSubtitulo>
              {loja.companyName}
            </AlfredoStyles.LojaSubtitulo>

            {loja.periodTypes.length === 0 ? (
              <AlfredoStyles.LojaSemPeriodos>
                Esta loja ainda não tem período de caixa.
              </AlfredoStyles.LojaSemPeriodos>
            ) : (
              <AlfredoStyles.PeriodosGrid>
                {loja.periodTypes.map((periodo: any) => (
                  <AlfredoStyles.PeriodoCard
                    key={periodo.periodTypeUuid}
                    onClick={() => handleOpenModal("periodType", periodo)}
                  >
                    <AlfredoStyles.PeriodoTituloLinha>
                      <AlfredoStyles.PeriodoTitulo>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-base)" }}
                        >
                          schedule
                        </AlfredoStyles.MaterialIcons>
                        {periodo.periodTypeName || "Período sem nome"}
                      </AlfredoStyles.PeriodoTitulo>
                      <AlfredoStyles.StatusBadge $ativo={!!periodo.periodTypeStatus}>
                        {periodo.periodTypeStatus ? "Ativo" : "Inativo"}
                      </AlfredoStyles.StatusBadge>
                    </AlfredoStyles.PeriodoTituloLinha>

                    <AlfredoStyles.PeriodoMeta>
                      {/*
                        A contagem de `periodTypeInfo` é crua de propósito: são
                        as linhas de `acd."ActPeriodTypeInfo"` que este período
                        tem hoje. Traduzir cada tipo em "descontos", "taxas" etc.
                        exigiria um de-para que este repo não tem — e inventar o
                        rótulo errado seria pior do que mostrar a contagem.
                      */}
                      <AlfredoStyles.MetaItem>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-base)" }}
                        >
                          list_alt
                        </AlfredoStyles.MaterialIcons>
                        {contarConfiguracoes(periodo)}
                      </AlfredoStyles.MetaItem>

                      <AlfredoStyles.MetaItem>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-base)" }}
                        >
                          update
                        </AlfredoStyles.MaterialIcons>
                        Atualizado em {formatarDataHora(periodo.updatedAt)}
                      </AlfredoStyles.MetaItem>
                    </AlfredoStyles.PeriodoMeta>
                  </AlfredoStyles.PeriodoCard>
                ))}
              </AlfredoStyles.PeriodosGrid>
            )}
          </AlfredoStyles.LojaCard>
        ))
      )}

      {totalPeriodos > 0 && (
        <AlfredoStyles.Contador>
          <strong>{totalPeriodos}</strong> período
          {totalPeriodos === 1 ? "" : "s"} em{" "}
          <strong>{lojasComPeriodo}</strong> loja
          {lojasComPeriodo === 1 ? "" : "s"}
        </AlfredoStyles.Contador>
      )}

      {/*
        Aviso permanente, e não um "em breve" escondido: o modal de período abre
        e mostra tudo, mas NÃO grava. `acd."ActPeriodTypeInfo"` não é exposta por
        nenhum router do backend (conferido em common/all.routes.ts), então o
        Salvar recusa por escrito ANTES de qualquer requisição, em vez de fechar
        fingindo sucesso. Quem lê isto aqui não perde trabalho digitado.
      */}
      <AlfredoStyles.Aviso $tom="atencao">
        <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-lg)" }}>
          warning
        </AlfredoStyles.MaterialIcons>
        <span>
          A gravação de período ainda não tem rota no servidor — a tabela
          ActPeriodTypeInfo não é exposta por nenhum router do backend. Esta tela
          abre para <strong>leitura e conferência</strong>: ao clicar em Salvar,
          o sistema devolve uma recusa nomeada e nada é enviado, em vez de fingir
          que gravou.
        </span>
      </AlfredoStyles.Aviso>
    </AlfredoStyles.FieldsContainer>
  );
};

export default Periodos;
